import React, { Fragment, useMemo, useCallback, CSSProperties } from 'react';
import filter from 'lodash/filter';

import {
  IsFetched,
  ErrorMessage,
  FetchItemsLoadMoreItems
} from '../../../../../types';
import { TeamNanoID } from '../../../../teams/teamsTypes';
import {
  LifestyleNanoID,
  FetchLifestylesFilters,
  ChangeLifestylesFiltersFunc,
  FetchLifestylesLimit,
  FetchLifestylesPage,
  UpdateLifestyleCacheAction,
  OnLifestyleSimilarSearchAction
} from '../../../../lifestyles/lifestylesTypes';
import {
  ThreeDStockLifestylesListLifestyles,
  ThreeDStockLifestylesListLifestyle,
  ThreeDStockLifestylesListLifestylesSet
} from './ThreeDStockLifestylesList.types';
import {
  FetchLifestylesSetsCacheKeys,
  UpdateLifestylesSetCacheAction
} from '../../../../lifestylesSets/lifestylesSetsTypes';

import { ThreeDStockLifestylesListItem } from '../ThreeDStockLifestylesListItem';
import { ThreeDStockLifestylesListLightboxTitle } from './components/ThreeDStockLifestylesListLightboxTitle';
import { ThreeDStockLifestylesListLightboxBottomButtons } from './components/ThreeDStockLifestylesListLightboxBottomButtons';

import { AlertMessage } from '../../../../../helpers/AlertMessage';
import { Loading } from '../../../../../helpers/Loading';
import { NoResults } from '../../../../../helpers/NoResults';
import {
  LightboxWrapper,
  useLightboxWrapper,
  LightboxRenderImageTitle,
  LightboxRenderCustomButtons
} from '../../../../../helpers/LightboxWrapper';
import { LoadingGridSkeleton } from '../../../../../helpers/LoadingGridSkeleton';
import { LoadMoreButtonHelper } from '../../../../../helpers/buttons/LoadMoreButtonHelper';

import { Files } from '../../../../../utils/Files';

import { LifestylesPermissions } from '../../../../lifestyles/lifestylesConstants';

type ThreeDStockLifestylesListProps = {
  cellMinSize: number;
  onLifestyleEditButtonMouseEnter?: (lifestyleNanoId: LifestyleNanoID) => void;
  lifestyles: ThreeDStockLifestylesListLifestyles;
  lifestylesError: ErrorMessage;
  lifestylesFetched: IsFetched;
  lifestylesIsPlaceholderData: boolean;
  lifestylesTotalCount: number;
  updateLifestyleCache?: UpdateLifestyleCacheAction<ThreeDStockLifestylesListLifestyle>;
  lifestylesFilters: FetchLifestylesFilters;
  changeLifestylesFilters: ChangeLifestylesFiltersFunc;
  hasNextLifestylesPage: boolean;
  lifestylesPage: FetchLifestylesPage;
  lifestylesLimit: FetchLifestylesLimit;
  loadMoreLifestyles: FetchItemsLoadMoreItems;
  lifestylesFetchingNextPage: boolean;
  lifestylesSet: ThreeDStockLifestylesListLifestylesSet | null;
  lifestylesSetCacheKeys?: FetchLifestylesSetsCacheKeys;
  updateLifestylesSetCache: UpdateLifestylesSetCacheAction<ThreeDStockLifestylesListLifestylesSet>;
  onLifestyleSimilarSearchFilter?: OnLifestyleSimilarSearchAction;
  isMyLibrary?: boolean;
  companyNanoId?: TeamNanoID;
  withRealAspectRatio?: boolean;
};

function ThreeDStockLifestylesList({
  cellMinSize,
  onLifestyleEditButtonMouseEnter,
  lifestyles,
  lifestylesError,
  lifestylesFetched,
  lifestylesIsPlaceholderData,
  lifestylesTotalCount,
  updateLifestyleCache,
  lifestylesFilters,
  changeLifestylesFilters,
  hasNextLifestylesPage,
  lifestylesPage,
  lifestylesLimit,
  loadMoreLifestyles,
  lifestylesFetchingNextPage,
  lifestylesSet,
  updateLifestylesSetCache,
  onLifestyleSimilarSearchFilter,
  lifestylesSetCacheKeys,
  companyNanoId,
  isMyLibrary,
  withRealAspectRatio
}: ThreeDStockLifestylesListProps) {
  const lifestylesInLightbox =
    useMemo<ThreeDStockLifestylesListLifestyles>(() => {
      return filter<ThreeDStockLifestylesListLifestyle>(
        lifestyles,
        (lifestyle) =>
          lifestyle.image?.file && Files.isImage(lifestyle.image.file)
      );
    }, [lifestyles]);

  const {
    handleLightboxClose,
    handleLightboxNext,
    handleLightboxOpen,
    handleLightboxOpenOnSlide,
    handleLightboxPrev,
    index,
    imagesCount,
    imageItem,
    lightBoxOpened,
    mainSrc,
    prevSrc,
    nextSrc
  } = useLightboxWrapper({
    items: lifestylesInLightbox
  });

  const renderImageTitle = useCallback<LightboxRenderImageTitle>(
    ({ index }) => {
      const lifestyle = lifestylesInLightbox[index];
      if (!lifestyle) {
        return null;
      }

      return <ThreeDStockLifestylesListLightboxTitle lifestyle={lifestyle} />;
    },
    [lifestylesInLightbox]
  );

  const renderLightboxButtons = useCallback<LightboxRenderCustomButtons>(
    ({ index }) => {
      const lifestyle = lifestylesInLightbox[index];
      if (!lifestyle) {
        return [];
      }

      return (
        <ThreeDStockLifestylesListLightboxBottomButtons
          lifestyle={lifestyle}
          lifestylesSet={lifestylesSet}
          lifestylesSetCacheKeys={lifestylesSetCacheKeys}
          updateLifestylesSetCache={updateLifestylesSetCache}
          updateLifestyleCache={updateLifestyleCache}
        />
      );
    },
    [
      lifestylesInLightbox,
      lifestylesSet,
      lifestylesSetCacheKeys,
      updateLifestylesSetCache,
      updateLifestyleCache
    ]
  );

  const wrapperStyle = useMemo(() => {
    return {
      '--tw-grid-cell-min-size': `${cellMinSize}px`
    };
  }, [cellMinSize]);

  return (
    <Fragment>
      <AlertMessage addClassName="m-4" message={lifestylesError} />
      <LoadingGridSkeleton
        loaded={lifestylesIsPlaceholderData || lifestylesFetched}
      >
        {lifestylesTotalCount === 0 && <NoResults addErrorClassName="m-4" />}
        {lifestylesTotalCount > 0 && (
          <div className="flex flex-col space-y-2">
            <Loading
              loaded={!lifestylesIsPlaceholderData}
              addClass="self-center"
            />
            <div
              className="grid grid-cols-auto-fill auto-rows-min gap-5"
              style={wrapperStyle as CSSProperties}
            >
              {lifestyles.map((lifestyle) => (
                <ThreeDStockLifestylesListItem
                  key={lifestyle.uuid}
                  lifestyle={lifestyle}
                  updateLifestyleCache={updateLifestyleCache}
                  onLifestyleEditButtonMouseEnter={
                    onLifestyleEditButtonMouseEnter
                  }
                  onLightboxOpen={handleLightboxOpenOnSlide}
                  lifestylesSet={lifestylesSet}
                  updateLifestylesSetCache={updateLifestylesSetCache}
                  lifestylesSetCacheKeys={lifestylesSetCacheKeys}
                  companyNanoId={companyNanoId}
                  isMyLibrary={isMyLibrary}
                  withRealAspectRatio={withRealAspectRatio}
                  onLifestyleSimilarSearchFilter={
                    onLifestyleSimilarSearchFilter
                  }
                />
              ))}
            </div>

            {hasNextLifestylesPage && (
              <LoadMoreButtonHelper
                action={
                  LifestylesPermissions.READ_LIFESTYLES_LOAD_MORE_BUTTON_ITEMS_COUNT
                }
                dataGa="lifestyles-load-more-button"
                itemsTotalCount={lifestylesTotalCount}
                page={lifestylesPage}
                limit={lifestylesLimit}
                loadMoreItems={loadMoreLifestyles}
                itemsFetchingNextPage={lifestylesFetchingNextPage}
              />
            )}
          </div>
        )}
      </LoadingGridSkeleton>
      <LightboxWrapper
        handleLightboxClose={handleLightboxClose}
        handleLightboxNext={handleLightboxNext}
        handleLightboxOpen={handleLightboxOpen}
        handleLightboxPrev={handleLightboxPrev}
        index={index}
        imagesCount={imagesCount}
        imageItem={imageItem}
        lightBoxOpened={lightBoxOpened}
        mainSrc={mainSrc}
        nextSrc={nextSrc}
        prevSrc={prevSrc}
        renderImageTitle={renderImageTitle}
        renderCustomButtons={renderLightboxButtons}
        withFullScreenButton
      />
    </Fragment>
  );
}

export default ThreeDStockLifestylesList;
